import {
  NormalizedAlert,
  AlertResponseMetadata,
  AlertSeverity,
} from '../types/alert';
import { AlertCache } from './alertCache';

interface UKFloodItem {
  '@id': string;
  description: string;
  eaAreaName?: string;
  floodAreaUri?: string;
  message?: string;
  severity: string; // e.g. "Severe Flood Warning", "Flood Warning", "Flood Alert"
  severityLevel: number; // 1: severe (danger to life), 2: warning, 3: alert, 4: removed
  timeMessageChanged?: string;
  timeRaised?: string;
  timeSeverityChanged?: string;
  floodArea?: {
    county?: string;
    lat?: number;
    long?: number;
    notation?: string;
    label?: string;
    riverOrSea?: string;
  };
}

interface UKFloodResponse {
  items: UKFloodItem[];
}

export class UKAdapter {
  private static endpoint = 'https://environment.data.gov.uk/flood-monitoring/id/floods';

  /**
   * Derive severity from UK Environment Agency severity levels
   */
  static deriveSeverity(level: number): AlertSeverity {
    switch (level) {
      case 1:
        return 'Extreme'; // Severe Flood Warning: Danger to life
      case 2:
        return 'Severe'; // Flood Warning: Immediate action required
      case 3:
        return 'Moderate'; // Flood Alert: Be prepared
      default:
        return 'Minor';
    }
  }

  /**
   * Normalize an incoming UK Environment Agency flood item
   */
  static normalizeFlood(item: UKFloodItem): NormalizedAlert | null {
    if (!item) return null;

    const id = item['@id'] || `uk_flood_${item.floodArea?.notation || Date.now()}`;
    const severity = this.deriveSeverity(item.severityLevel);
    const eventTime = item.timeSeverityChanged || item.timeRaised || new Date().toISOString();
    const isCleared = item.severityLevel === 4;

    const lat = item.floodArea?.lat;
    const lon = item.floodArea?.long;
    const centroid: [number, number] | null =
      typeof lat === 'number' && typeof lon === 'number' ? [lat, lon] : null;

    const areaName =
      item.floodArea?.label ||
      item.floodArea?.county ||
      item.eaAreaName ||
      'England & Wales Flood Zone';

    const instruction =
      item.severityLevel === 1
        ? 'UK Emergency Instruction: DANGER TO LIFE. Evacuate immediately if advised by emergency services. Call 999 if in immediate danger.'
        : item.severityLevel === 2
        ? 'UK Flood Warning: Flooding is expected. Turn off gas, water and electricity. Move family, pets and cars to safety.'
        : 'UK Flood Alert: Flooding is possible. Prepare a bag that includes medicines and insurance documents. Check river levels.';

    return {
      id: `uk_${id.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
      event: item.severity || 'Flood Warning',
      headline: `${item.severity || 'Flood Alert'}: ${areaName}`,
      description: item.message || item.description || 'Active flood bulletin issued by the Environment Agency.',
      instruction,
      severity,
      urgency: item.severityLevel <= 2 ? 'Immediate' : 'Expected',
      certainty: 'Observed',
      senderName: 'UK Environment Agency',
      areaDesc: `${areaName}, United Kingdom`,
      geometry: centroid
        ? {
            type: 'Point',
            coordinates: [centroid[1], centroid[0]],
          }
        : null,
      centroid,
      effective: eventTime,
      onset: eventTime,
      expires: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
      ends: isCleared ? new Date().toISOString() : null,
      status: 'Actual',
      messageType: isCleared ? 'Cancel' : 'Alert',
      category: 'Env',
      disruptionType: 'Flood',
      sourceAgency: 'EA_UK',
      country: 'GB',
      sourceUrl: item['@id'] || 'https://check-for-flooding.service.gov.uk',
      firstSeen: eventTime,
      lastSeen: new Date().toISOString(),
      isExpired: isCleared,
    };
  }

  /**
   * Fetch live UK flood warnings from Environment Agency
   */
  static async fetchAlerts(options?: {
    forceRefresh?: boolean;
  }): Promise<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }> {
    const cacheKey = 'uk_ea_flood_alerts';

    if (!options?.forceRefresh) {
      const cached = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (cached && !cached.isStale) {
        return {
          alerts: cached.data.alerts,
          metadata: {
            ...cached.data.metadata,
            isCached: true,
            cacheAgeSeconds: cached.ageSeconds,
          },
        };
      }
    }

    try {
      const res = await fetch(this.endpoint, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`UK EA HTTP ${res.status}: ${res.statusText}`);
      }

      const json: UKFloodResponse = await res.json();
      const rawItems = Array.isArray(json.items) ? json.items : [];

      const normalized: NormalizedAlert[] = [];
      for (const item of rawItems) {
        const norm = this.normalizeFlood(item);
        if (norm) normalized.push(norm);
      }

      const metadata: AlertResponseMetadata = {
        source: 'UK Environment Agency (data.gov.uk)',
        totalAlerts: normalized.length,
        lastChecked: new Date().toISOString(),
        isCached: false,
        cacheAgeSeconds: 0,
        statusMessage: 'Verified live response from UK Environment Agency',
      };

      AlertCache.set(cacheKey, { alerts: normalized, metadata });

      return { alerts: normalized, metadata };
    } catch (err: any) {
      const fallback = AlertCache.get<{ alerts: NormalizedAlert[]; metadata: AlertResponseMetadata }>(cacheKey);
      if (fallback) {
        return {
          alerts: fallback.data.alerts.map((a) => ({ ...a, isStale: true })),
          metadata: {
            ...fallback.data.metadata,
            isCached: true,
            isFallback: true,
            cacheAgeSeconds: fallback.ageSeconds,
            statusMessage: `UK EA warning: ${err.message}. Showing cached data.`,
          },
        };
      }

      // Return empty gracefully rather than breaking global feeds
      return {
        alerts: [],
        metadata: {
          source: 'UK Environment Agency (data.gov.uk)',
          totalAlerts: 0,
          lastChecked: new Date().toISOString(),
          isCached: false,
          statusMessage: `UK Feed Standby: ${err.message}`,
        },
      };
    }
  }
}

